"use client";

import localforage from "localforage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { structuredCloneWithFallback, wait } from "../lib/utils";

type Setter = (value: unknown) => Promise<unknown>;

export function useLocalforage<T extends Array<unknown>>(keys: string[], options: LocalForageOptions = {}) {
  const channelId = useMemo(
    () => `localforage-${options.storeName ?? "prime"}-${options.name ?? "default"}`,
    [options.storeName, options.name]
  );
  const [store, setStore] = useState<LocalForage | null>(null);
  const [values, setValues] = useState<T>([] as unknown as T);
  const [setters, setSetters] = useState<Setter[]>([] as Setter[]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Use ref to track keys without triggering re-renders
  const keysRef = useRef<string[]>(keys);
  keysRef.current = keys;

  const refresh = useCallback(async () => {
    if (store) {
      const currentKeys = keysRef.current;
      const values = await Promise.all(currentKeys.map((key: string) => store.getItem(key)));
      const setters = currentKeys.map((key: string, index: number) => async (value: unknown) => {
        const result = await store.setItem(key, value);
        setValues((prev) => {
          const next = [...prev] as unknown as T;
          next[index] = value;
          return next;
        });
        const channel = getChannel(channelId, "send");
        channel.postMessage({ key, value });
        return result;
      });

      setValues(values as T);
      setSetters(setters);
      return values;
    }
  }, [store, channelId]);
  const setItem = useCallback(
    async (key: string, value: unknown) => {
      if (store) {
        const result = await store.setItem(key, value);
        const channel = getChannel(channelId, "send");
        channel.postMessage({ key, value });
        return result;
      }
    },
    [channelId, store]
  );

  // Track if we've done the initial load
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once when store becomes available
    if (store && !hasLoadedRef.current) {
      hasLoadedRef.current = true;

      (async () => {
        const currentKeys = keysRef.current;
        const values = await Promise.all(currentKeys.map((key: string) => store.getItem(key)));
        const setters = currentKeys.map((key: string, index: number) => async (value: unknown) => {
          const result = await store.setItem(key, value);
          setValues((prev) => {
            const next = [...prev] as unknown as T;
            next[index] = value;
            return next;
          });
          const channel = getChannel(channelId, "send");
          channel.postMessage({ key, value });
          return result;
        });

        setValues(values as T);
        setSetters(setters);
        await wait(10);
        setIsLoaded(true);
      })();
    }
  }, [store, channelId]);

  useEffect(() => {
    if (!store) {
      setStore(localforage.createInstance(options));
    }
  }, [options.name, options.storeName, options.driver, options.version, options.size, options.description]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentKeys = keysRef.current;
    const channel = getChannel(channelId, "receive");

    function listener({ data }: { data: { key: string; value: unknown } }) {
      if (currentKeys.includes(data.key)) {
        const { key, value } = data;
        setValues((values) => {
          const index = currentKeys.indexOf(key);
          if (index > -1) {
            values[index] = value;
          }
          return structuredCloneWithFallback<T>(values);
        });
      }
    }

    channel.addEventListener("message", listener);

    return () => {
      channel.removeEventListener("message", listener);
    };
  }, [channelId]);

  return { values, refresh, setters, setItem: setters[0], store, isLoaded };
}

const CHANNELS = new Map<string, BroadcastChannel>();

function getChannel(channelId: string, sendOrReceive: "send" | "receive") {
  const id = `${channelId}-${sendOrReceive}`;

  if (!CHANNELS.has(id)) {
    CHANNELS.set(id, new BroadcastChannel(channelId));
  }

  return CHANNELS.get(id) as BroadcastChannel;
}

export function closeChannel(channelId: string, sendOrReceive: "send" | "receive") {
  const id = `${channelId}-${sendOrReceive}`;
  const channel = CHANNELS.get(id);

  if (channel) {
    CHANNELS.delete(id);
    channel.close();
  }
}

